/**
 * Authentication Service
 *
 * Handles OAuth flows (Google, Microsoft) and 2FA verification.
 * Integrates with backend auth routes.
 */

const AUTH_KEY = 'agenticed_admin_auth';
const PENDING_2FA_KEY = 'agenticed_pending_2fa';

/**
 * Auth service for admin portal
 */
class AuthService {
    constructor() {
        this.session = null;
        this._loadSession();
    }

    /**
     * Load session from localStorage
     */
    _loadSession() {
        try {
            const stored = localStorage.getItem(AUTH_KEY);
            if (stored) {
                this.session = JSON.parse(stored);
                // Check if session is expired
                if (this.session.expiresAt && new Date(this.session.expiresAt) < new Date()) {
                    this.logout();
                }
            }
        } catch {
            this.session = null;
        }
    }

    /**
     * Save session to localStorage
     */
    _saveSession(session) {
        this.session = session;
        localStorage.setItem(AUTH_KEY, JSON.stringify(session));
    }

    /**
     * Get Google OAuth URL
     * @param {string} portalType - 'staff' or 'district'
     * @param {string} tenantId - Optional tenant ID for district login
     */
    async getGoogleAuthUrl(portalType, tenantId = null) {
        const params = new URLSearchParams();
        if (tenantId) params.append('tenantId', tenantId);

        const response = await fetch(`/api/auth/google/url?${params.toString()}`);
        if (!response.ok) {
            throw new Error('Failed to get Google OAuth URL');
        }
        const data = await response.json();

        // Store portal type for callback
        sessionStorage.setItem('oauth_portal_type', portalType);
        if (tenantId) sessionStorage.setItem('oauth_tenant_id', tenantId);

        return data.url;
    }

    /**
     * Get Microsoft OAuth URL
     * @param {string} portalType - 'staff' or 'district'
     * @param {string} tenantId - Optional tenant ID for district login
     */
    async getMicrosoftAuthUrl(portalType, tenantId = null) {
        const params = new URLSearchParams();
        if (tenantId) params.append('tenantId', tenantId);
        // Use 'organizations' for work/school accounts
        params.append('domainHint', 'organizations');

        const response = await fetch(`/api/auth/microsoft/url?${params.toString()}`);
        if (!response.ok) {
            throw new Error('Failed to get Microsoft OAuth URL');
        }
        const data = await response.json();

        // Store portal type for callback
        sessionStorage.setItem('oauth_portal_type', portalType);
        if (tenantId) sessionStorage.setItem('oauth_tenant_id', tenantId);

        return data.url;
    }

    /**
     * Handle OAuth callback - process token from URL
     * @param {string} token - JWT token from OAuth callback
     * @returns {Object} { requiresTwoFactor, sessionId, user }
     */
    async handleOAuthCallback(token) {
        const portalType = sessionStorage.getItem('oauth_portal_type') || 'staff';
        const tenantId = sessionStorage.getItem('oauth_tenant_id');

        // Clean up session storage
        sessionStorage.removeItem('oauth_portal_type');
        sessionStorage.removeItem('oauth_tenant_id');

        // Decode token to get user info (basic decode, not verification)
        const payload = this._decodeToken(token);

        if (payload.twoFactorEnabled) {
            // Store pending 2FA session
            localStorage.setItem(PENDING_2FA_KEY, JSON.stringify({
                token,
                userId: payload.userId,
                portalType,
                tenantId
            }));

            return {
                requiresTwoFactor: true,
                sessionId: payload.sessionId,
                userId: payload.userId
            };
        }

        // No 2FA required - complete login
        this._saveSession({
            token,
            userId: payload.userId,
            email: payload.email,
            // Prefer the JWT's own type claim — the GTA login carries the resolver-derived
            // subject type ('district' | 'vendor'); platform OAuth logins have no type claim
            // and fall back to the pre-selected portal type.
            type: payload.type || portalType,
            role: payload.role,
            tenantId: tenantId || payload.tenantId,
            twoFactorEnabled: false,
            gta: !!payload.gta,   // marks a GTA session so the callback routes to /gta
            expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null
        });

        return {
            requiresTwoFactor: false,
            user: payload
        };
    }

    /**
     * Verify 2FA code
     * @param {string} code - TOTP code from authenticator
     */
    async verify2FA(code) {
        const pending = this._getPending2FA();
        if (!pending) {
            throw new Error('No pending 2FA verification');
        }

        const response = await fetch('/api/auth/2fa/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: pending.userId,
                code
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Invalid verification code');
        }

        // 2FA verified - complete login
        const payload = this._decodeToken(pending.token);
        this._saveSession({
            token: pending.token,
            userId: pending.userId,
            email: payload.email,
            // Carry the JWT's own axis + role + GTA marker through 2FA, exactly as the
            // no-2FA path (handleOAuthCallback) does. Dropping them here strips gta/type
            // from a GTA session so the completion routes it to a dead platform portal
            // instead of /gta — the staff-login regression this path caused.
            type: payload.type || pending.portalType,
            role: payload.role,
            tenantId: pending.tenantId || payload.tenantId,
            twoFactorEnabled: true,
            gta: !!payload.gta,
            expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null
        });

        // Clean up pending 2FA
        localStorage.removeItem(PENDING_2FA_KEY);

        return { success: true };
    }

    /**
     * Verify 2FA backup code
     * @param {string} code - Backup code
     */
    async verify2FABackup(code) {
        const pending = this._getPending2FA();
        if (!pending) {
            throw new Error('No pending 2FA verification');
        }

        const response = await fetch('/api/auth/2fa/verify-backup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: pending.userId,
                code
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Invalid backup code');
        }

        const result = await response.json();

        // 2FA verified - complete login
        const payload = this._decodeToken(pending.token);
        this._saveSession({
            token: pending.token,
            userId: pending.userId,
            email: payload.email,
            // Same as verify2FA: preserve the JWT's axis + role + GTA marker so a GTA
            // session completing 2FA via a backup code still routes to /gta.
            type: payload.type || pending.portalType,
            role: payload.role,
            tenantId: pending.tenantId || payload.tenantId,
            twoFactorEnabled: true,
            gta: !!payload.gta,
            expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null
        });

        // Clean up pending 2FA
        localStorage.removeItem(PENDING_2FA_KEY);

        return {
            success: true,
            remainingBackupCodes: result.remainingBackupCodes,
            warning: result.warning
        };
    }

    /**
     * Get pending 2FA data
     */
    _getPending2FA() {
        try {
            const stored = localStorage.getItem(PENDING_2FA_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }

    /**
     * Check if there's a pending 2FA verification
     */
    hasPending2FA() {
        return !!this._getPending2FA();
    }

    /**
     * Cancel pending 2FA
     */
    cancelPending2FA() {
        localStorage.removeItem(PENDING_2FA_KEY);
    }

    /**
     * Decode JWT token (basic decode, not verification)
     */
    _decodeToken(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(atob(base64));
            return payload;
        } catch {
            return {};
        }
    }

    /**
     * Vendor login with API key
     */
    async loginWithApiKey(apiKey) {
        const response = await fetch('/api/sdk/vendors/me', {
            headers: { 'X-Api-Key': apiKey }
        });

        if (!response.ok) {
            throw new Error('Invalid API key');
        }

        const data = await response.json();

        this._saveSession({
            apiKey,
            type: 'vendor',
            vendorId: data.vendor?.id,
            vendorName: data.vendor?.name,
            email: data.vendor?.email
        });

        return data;
    }

    /**
     * Logout - clear session
     */
    async logout() {
        // Try to destroy server session
        if (this.session?.token) {
            try {
                // Send the TOKEN, not a session id. This used to post
                // `{ sessionId: this.session.sessionId }` — a field this service never stores —
                // so the server received `undefined`, answered 400, and the 7-day JWT stayed
                // live: "Sign out" did not revoke the server session. The server now reads the
                // session id from the token's own `sid` claim, so there is nothing to send and
                // nothing to store.
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${this.getToken()}` }
                });
            } catch {
                // Ignore errors during logout
            }
        }

        this.session = null;
        localStorage.removeItem(AUTH_KEY);
        localStorage.removeItem(PENDING_2FA_KEY);
    }

    /**
     * Check if user is logged in
     */
    isLoggedIn() {
        return !!this.session;
    }

    /**
     * Get current session
     */
    getSession() {
        return this.session;
    }

    /**
     * Get portal type
     */
    getType() {
        if (this.session?.type) return this.session.type;
        // Infer portal type from role for sessions that don't explicitly store type
        // (OAuth logins, legacy sessions, some API-minted tokens)
        const role = this.getRole();
        if (!role) return null;
        if (['district', 'admin', 'superintendent', 'cio', 'counselor', 'principal', 'teacher'].includes(role)) return 'district';
        if (['platform_admin', 'staff'].includes(role)) return 'staff';
        if (['vendor'].includes(role)) return 'vendor';
        return null;
    }

    /**
     * Get auth token for API requests
     */
    getToken() {
        return this.session?.token || null;
    }

    /**
     * Get API key for vendor requests
     */
    getApiKey() {
        return this.session?.apiKey || null;
    }

    // =========================================================================
    // 2FA Setup Methods (for settings page)
    // =========================================================================

    /**
     * Set up 2FA for current user
     */
    async setup2FA() {
        const response = await fetch('/api/auth/2fa/setup', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.getToken()}`
            },
            body: JSON.stringify({
                userId: this.session?.userId,
                email: this.session?.email
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to set up 2FA');
        }

        return response.json();
    }

    /**
     * Complete 2FA setup by verifying code
     */
    async completeSetup2FA(code) {
        const response = await fetch('/api/auth/2fa/verify', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.getToken()}`
            },
            body: JSON.stringify({
                userId: this.session?.userId,
                code,
                isSetup: true
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Invalid verification code');
        }

        // Update session to reflect 2FA enabled
        this._saveSession({
            ...this.session,
            twoFactorEnabled: true
        });

        return response.json();
    }

    /**
     * Disable 2FA
     */
    async disable2FA(code) {
        const response = await fetch('/api/auth/2fa/disable', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.getToken()}`
            },
            body: JSON.stringify({
                userId: this.session?.userId,
                code
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to disable 2FA');
        }

        // Update session to reflect 2FA disabled
        this._saveSession({
            ...this.session,
            twoFactorEnabled: false
        });

        return response.json();
    }

    /**
     * Regenerate backup codes
     */
    async regenerateBackupCodes(code) {
        const response = await fetch('/api/auth/2fa/regenerate-backup', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.getToken()}`
            },
            body: JSON.stringify({
                userId: this.session?.userId,
                code
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to regenerate backup codes');
        }

        return response.json();
    }

    /**
     * Get active sessions
     */
    async getSessions() {
        const response = await fetch(`/api/auth/sessions?userId=${this.session?.userId}`, {
            headers: {
                'Authorization': `Bearer ${this.getToken()}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to get sessions');
        }

        return response.json();
    }

    /**
     * Revoke a session
     */
    async revokeSession(sessionId) {
        const response = await fetch(`/api/auth/sessions/${sessionId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${this.getToken()}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to revoke session');
        }

        return response.json();
    }

    // =========================================================================
    // Role & Permission Helpers (3-Zone Model)
    // =========================================================================

    /**
     * Get current user's role from session.
     * Checks demoRole first (demo mode), then decodes JWT for 'role' claim.
     * @returns {string|null} Role key: 'district','superintendent','cio','counselor','teacher', etc.
     */
    getRole() {
        if (!this.session) return null;
        if (this.session.demoRole) return this.session.demoRole;
        if (this.session.token) {
            const payload = this._decodeToken(this.session.token);
            return payload.role || payload.demoRole || 'district';
        }
        return null;
    }

    /**
     * Check if current user has the specified role
     */
    hasRole(role) {
        return this.getRole() === role;
    }

    /**
     * Check if current user has ANY of the specified roles
     */
    hasAnyRole(roles) {
        const currentRole = this.getRole();
        return roles.includes(currentRole);
    }

    /**
     * Get permissions for the current role
     */
    getPermissions() {
        const role = this.getRole();
        return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['teacher'] || [];
    }

    /**
     * Check if current user has a specific permission
     */
    hasPermission(permission) {
        return this.getPermissions().includes(permission);
    }

    /**
     * Check if current role has access to the Administration zone
     */
    hasAdminAccess() {
        return this.hasPermission('view_admin');
    }
}

// Role → permission mappings for the 3-Zone Model
const ROLE_PERMISSIONS = {
    district: [
        'view_admin', 'view_overview', 'view_superintendent', 'view_cio',
        'view_student_services', 'view_operations', 'view_compliance',
        'manage_users', 'manage_settings', 'manage_workspace'
    ],
    superintendent: [
        'view_admin', 'view_overview', 'view_superintendent', 'view_compliance'
    ],
    cio: [
        'view_admin', 'view_overview', 'view_cio', 'view_operations'
    ],
    counselor: [
        'view_admin', 'view_student_services'
    ],
    teacher: []
};

/**
 * The post-login destination for a completed session. A GTA session (`gta: true`) always
 * goes to `/gta`, which dispatches by axis to the right landing surface (district home,
 * vendor console, staff ops) via landingPath — regardless of the pre-selected portal type,
 * and whether login completed with or without 2FA. A non-GTA platform session routes by its
 * portal type.
 *
 * Both completion sites (OAuthCallback and TwoFactorVerify) route through THIS one resolver,
 * so a newly added axis can never be honored in one place and forgotten in the other — the
 * exact drift that stranded staff GTA logins on a dead `/staff` route (staff was added after
 * the callback's original district/vendor branching).
 */
export function postLoginRedirect(session) {
    if (session?.gta) return '/gta';
    const type = session?.type;
    if (type === 'vendor') return '/vendor';
    if (type === 'district') return '/district';
    return '/staff';
}

// Export singleton instance
const authService = new AuthService();
export default authService;

// Also export for legacy compatibility
export const auth = {
    login(type, identifier) {
        // Legacy demo login - will be removed
        localStorage.setItem(AUTH_KEY, JSON.stringify({
            type,
            identifier,
            loggedInAt: Date.now()
        }));
    },
    logout() {
        authService.logout();
    },
    getSession() {
        return authService.getSession();
    },
    isLoggedIn() {
        return authService.isLoggedIn();
    },
    getType() {
        return authService.getType();
    }
};

// Make auth available globally for logout from Layout
if (typeof window !== 'undefined') {
    window.agenticedAuth = auth;
}
